import type { FormEvent, ReactNode } from 'react'
import type { Treatment } from '@/types/clinic'

import { useState } from 'react'
import { toast } from 'sonner'
import { useClinic } from '@/lib/clinic/store'
import { roundMoney } from '@/lib/format'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import FormField from '@/components/app/form-field'
import SimpleSelect from '@/components/app/simple-select'

import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog'

import { APPOINTMENT_DURATIONS } from '@/constants/clinic'

interface TreatmentDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    treatment?: Treatment
}

const TreatmentDialog = ({ open, onOpenChange, treatment }: TreatmentDialogProps): ReactNode => {
    const { data, actions } = useClinic()
    const [name, setName] = useState(treatment?.name ?? '')
    const [price, setPrice] = useState(treatment ? String(treatment.price) : '')
    const [duration, setDuration] = useState(String(treatment?.duration ?? 30))
    const [description, setDescription] = useState(treatment?.description ?? '')
    const [active, setActive] = useState(treatment?.active ?? true)
    const [submitted, setSubmitted] = useState(false)

    const duplicate = data.treatments.some(item => item.id !== treatment?.id && item.name.trim().toLowerCase() === name.trim().toLowerCase())
    const priceValue = Number(price)
    const errors = {
        name: name.trim().length < 2 ? 'Enter the treatment name.' : duplicate ? 'A treatment with this name already exists.' : '',
        price: price === '' || Number.isNaN(priceValue) || priceValue < 0 ? 'Enter a price of zero or more.' : ''
    }

    const durations = Array.from(new Set([...APPOINTMENT_DURATIONS, Number(duration)])).sort((a, b) => a - b)

    const handleSubmit = (event: FormEvent) => {
        event.preventDefault()
        setSubmitted(true)
        if (errors.name || errors.price) return
        const input = {
            name: name.trim(),
            price: roundMoney(priceValue),
            duration: Number(duration) || 30,
            description: description.trim(),
            active
        }
        if (treatment) {
            actions.updateTreatment(treatment.id, input)
            toast.success('Treatment updated', { description: input.name })
        }
        else {
            actions.addTreatment(input)
            toast.success('Treatment added', { description: input.name })
        }
        onOpenChange(false)
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className='sm:max-w-lg'>
                <form onSubmit={handleSubmit} noValidate className='grid gap-5'>
                    <DialogHeader>
                        <DialogTitle>{treatment ? 'Edit treatment' : 'Add treatment'}</DialogTitle>
                        <DialogDescription>Prices are used when recording visits and generating bills.</DialogDescription>
                    </DialogHeader>

                    <div className='grid gap-4 sm:grid-cols-2'>
                        <FormField label='Treatment name' htmlFor='treatment-name' required error={submitted ? errors.name : ''} className='sm:col-span-2'>
                            <Input
                                id='treatment-name'
                                value={name}
                                onChange={event => setName(event.target.value)}
                                placeholder='Orthodontic consultation'
                                aria-invalid={submitted && Boolean(errors.name)}
                            />
                        </FormField>

                        <FormField label={`Price (${data.settings.billing.currency})`} htmlFor='treatment-price' required error={submitted ? errors.price : ''}>
                            <Input
                                id='treatment-price'
                                type='number'
                                inputMode='decimal'
                                min={0}
                                step='50'
                                value={price}
                                onChange={event => setPrice(event.target.value)}
                                placeholder='1500'
                                aria-invalid={submitted && Boolean(errors.price)}
                            />
                        </FormField>

                        <FormField label='Usual duration' htmlFor='treatment-duration' hint='Default length when booking'>
                            <SimpleSelect
                                id='treatment-duration'
                                value={duration}
                                onValueChange={setDuration}
                                options={durations.map(minutes => ({ value: String(minutes), label: `${minutes} minutes` }))}
                            />
                        </FormField>

                        <FormField label='Description' htmlFor='treatment-description' className='sm:col-span-2'>
                            <Textarea
                                id='treatment-description'
                                value={description}
                                onChange={event => setDescription(event.target.value)}
                                placeholder='What the treatment includes'
                                rows={3}
                            />
                        </FormField>

                        <div className='flex items-center justify-between gap-4 rounded-lg border p-3 sm:col-span-2'>
                            <div>
                                <Label htmlFor='treatment-active'>Active</Label>
                                <p className='mt-1 text-xs text-muted-foreground'>Inactive treatments are hidden when booking and billing.</p>
                            </div>
                            <Switch id='treatment-active' checked={active} onCheckedChange={setActive} />
                        </div>
                    </div>

                    <DialogFooter>
                        <DialogClose render={<Button type='button' variant='outline' />}>
                            Cancel
                        </DialogClose>
                        <Button type='submit'>{treatment ? 'Save changes' : 'Add treatment'}</Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    )
}

export default TreatmentDialog
